/*
  House of Zena — Base Utilities & Web Components
  Enterprise Hardened & Performance Optimized
*/

// Structured Telemetry & Diagnostics Logger
window.__ZENA_LOGGER__ = {
  levels: { debug: 0, info: 1, warn: 2, error: 3 },
  currentLevel: 2, // warn (production)
  log (level, message, data = null) {
    if (this.levels[level] >= this.currentLevel) {
      const timestamp = new Date().toISOString()
      const payload = { timestamp, level: level.toUpperCase(), message }
      if (data) payload.data = data
      if (level === 'error') {
        console.error('[ZENA]', payload)
      } else if (level === 'warn') {
        console.warn('[ZENA]', payload)
      }
    }
  }
}
window.zenaLog = (level, msg, data) => window.__ZENA_LOGGER__.log(level, msg, data)

window.theme = window.theme || {}
window.Shopify = window.Shopify || {}

// Init Bootstrap tooltips/popovers safely
if (window.bootstrap?.Tooltip) {
  document.querySelectorAll('[data-bs-toggle="tooltip"]')
    .forEach((el) => window.bootstrap.Tooltip.getOrCreateInstance(el))
}

if (window.bootstrap?.Popover) {
  document.querySelectorAll('[data-bs-toggle="popover"]')
    .forEach((el) => window.bootstrap.Popover.getOrCreateInstance(el))
}

// Debounce Utility
window.theme.debounce = function (callback, wait = 200) {
  let timeout
  return (...args) => {
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => callback.apply(context, args), wait)
  }
}

// Throttle Utility
window.theme.throttle = function (callback, timeFrame = 200) {
  let lastTime = 0
  return (...args) => {
    const now = Date.now()
    if (now - lastTime >= timeFrame) {
      callback.apply(this, args)
      lastTime = now
    }
  }
}

// Create cookie
window.theme.createCookie = function (name, value, days) {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setDate(date.getDate() + days)
    expires = '; expires=' + date.toUTCString()
  }
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax; Secure`
}

// Throttled Scroll Detection for Class Toggling
const handleGlobalScroll = () => {
  const scrollY = window.scrollY || window.pageYOffset || 0
  if (scrollY > 0) {
    document.documentElement.classList.add('has-scrolled')
  } else {
    document.documentElement.classList.remove('has-scrolled')
  }
  if ((window.innerHeight + Math.round(scrollY)) >= document.body.offsetHeight - 5) {
    document.documentElement.classList.add('has-scrolled-bottom')
  } else {
    document.documentElement.classList.remove('has-scrolled-bottom')
  }
}
window.addEventListener('scroll', window.theme.throttle(handleGlobalScroll, 100), { passive: true })

// Calculate "xx time ago"
window.theme.calcTimeAgo = function (timestamp) {
  const now = Date.now()
  const diff = now - timestamp
  const textTimes = window.theme?.locales?.times || {}

  let text

  if (diff < 60000) {
    text = textTimes.moments || 'a few moments'
  } else if (diff < 3.6e+6) {
    const min = Math.round(diff / 60000)
    text = min === 1
      ? `${min} ${textTimes.minute || 'minute'}`
      : `${min} ${textTimes.minutes || 'minutes'}`
  } else if (diff < 8.64e+7) {
    const hours = Math.round(diff / 3.6e+6)
    text = hours === 1
      ? `${hours} ${textTimes.hour || 'hour'}`
      : `${hours} ${textTimes.hours || 'hours'}`
  } else {
    const days = Math.round(diff / 8.64e+7)
    text = days === 1
      ? `${days} ${textTimes.day || 'day'}`
      : `${days} ${textTimes.days || 'days'}`
  }

  return `${text} ${textTimes.ago || 'ago'}`
}

// Enterprise formatMoney with full safety guards
window.Shopify.formatMoney = function (cents, moneyFormat = window.Shopify.moneyFormat || '₹{{amount}}') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '')
  }

  let value = ''
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const format = moneyFormat || '₹{{amount}}'
  const match = format.match(placeholderRegex)

  function defaultOption (opt, def) {
    return (typeof opt === 'undefined' ? def : opt)
  }

  function formatWithDelimiters (number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2)
    thousands = defaultOption(thousands, ',')
    decimal = defaultOption(decimal, '.')

    if (isNaN(number) || number == null) {
      return '0'
    }

    number = (number / 100.0).toFixed(precision)

    const parts = number.split('.')
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
    const centsPart = parts[1] ? (decimal + parts[1]) : ''

    return dollars + centsPart
  }

  const formatType = match ? match[1] : 'amount'
  switch (formatType) {
    case 'amount':
      value = formatWithDelimiters(cents, 2)
      break
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0)
      break
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',')
      break
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',')
      break
    default:
      value = formatWithDelimiters(cents, 2)
      break
  }

  const formatted = format.replace(placeholderRegex, value)
  if (!formatted.includes('₹') && !formatted.includes('Rs') && !formatted.includes('$') && !formatted.includes('€')) {
    return `₹${formatted}`
  }
  return formatted
}

// Resize images
window.Shopify.resizeImage = function (src, size, crop = '') {
  if (!src || typeof src !== 'string') return ''
  return src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
      let suffix = `_${size}`
      if (crop.length) {
        suffix += `_crop_${crop}`
      }
      return `${suffix}${match}`
    })
}

// Web Components
class OffcanvasMenu extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('.nav-link-collapse').forEach(btn => {
      btn.addEventListener('click', (event) => {
        event.preventDefault()
        const collapse = btn.closest('.nav-item').querySelector('.nav-collapse')
        window.bootstrap.Collapse.getOrCreateInstance(collapse).toggle()
      })
    })

    this.querySelectorAll('.btn-back').forEach(btn => {
      btn.addEventListener('click', (event) => {
        event.preventDefault()
        const collapse = btn.closest('.nav-collapse')
        window.bootstrap.Collapse.getOrCreateInstance(collapse).hide()
      })
    })
  }
}
customElements.define('offcanvas-menu', OffcanvasMenu)

class QtyControls extends HTMLElement {
  constructor () {
    super()

    // If inside cart, cart.js delegates quantity changes to prevent competing double-increments
    if (this.closest('#offcanvas-cart, .offcanvas-cart, .cart-item, #cart-items, .cart-container, cart-container, .cart-form, form[action*="/cart"]')) {
      return
    }

    this.btnMinus = this.querySelector('.btn-qty-minus')
    this.btnPlus = this.querySelector('.btn-qty-plus')
    this.input = this.querySelector('input')

    this.btnMinus?.addEventListener('click', () => {
      const current = Number(this.input.value) || 1
      if (current > 1) {
        this.input.value = current - 1
        this.input.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    this.btnPlus?.addEventListener('click', () => {
      const current = Number(this.input.value) || 0
      this.input.value = current + 1
      this.input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }
}
customElements.define('qty-controls', QtyControls)

class RecentlyViewed extends HTMLElement {
  constructor () {
    super()
    this.init()
  }

  init () {
    const list = this.querySelector('.product-list')
    if (!list) return

    let items = []
    try {
      items = JSON.parse(localStorage.getItem('ks-recently-viewed') || '[]')
    } catch (e) {
      items = []
    }

    if (!items.length) {
      this.setAttribute('hidden', 'hidden')
      return
    }

    const currentHandle = this.dataset.currentProductHandle
    const filtered = items.filter(handle => handle !== currentHandle).slice(0, 8)

    if (!filtered.length) {
      this.setAttribute('hidden', 'hidden')
      return
    }

    filtered.forEach(handle => {
      fetch(`/products/${handle}?section_id=product-card`)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html')
          const card = doc.querySelector('.product-card')
          if (card) list.appendChild(card)
        })
        .catch(() => {})
    })
  }
}
customElements.define('recently-viewed', RecentlyViewed)

class SwiperSlider extends HTMLElement {
  constructor () {
    super()

    if (window.Swiper) {
      this.initSwiper()
    } else {
      window.addEventListener('load', () => this.initSwiper())
    }
  }

  initSwiper () {
    if (!window.Swiper) return

    const configEl = this.querySelector('[data-swiper-config]')
    let config = {}
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent)
      } catch (e) {
        config = {}
      }
    }

    const container = this.querySelector('.swiper')
    if (container && !container.swiper) {
      // eslint-disable-next-line no-new
      new window.Swiper(container, config)
    }
  }
}
customElements.define('swiper-slider', SwiperSlider)

class AnimatedCountdown extends HTMLElement {
  constructor () {
    super()

    if (!this.dataset.time || !this.dataset.time.length) {
      return
    }

    const countDownDate = Number(this.dataset.time) * 1000
    const textTimes = window.theme.locales?.times || {}

    let d = textTimes.d || 'd'
    let h = textTimes.h || 'h'
    let m = textTimes.m || 'm'
    let s = textTimes.s || 's'

    if (this.dataset.format === 'long') {
      d = textTimes.days || 'days'
      h = textTimes.hours || 'hours'
      m = textTimes.minutes || 'minutes'
      s = textTimes.seconds || 'seconds'
    }

    this.timerId = setInterval(() => {
      const now = Date.now()
      const distance = countDownDate - now

      const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const min = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const sec = Math.floor((distance % (1000 * 60)) / 1000)

      const daysEl = this.querySelector('[data-days]')
      const hoursEl = this.querySelector('[data-hours]')
      const minEl = this.querySelector('[data-min]')
      const secEl = this.querySelector('[data-sec]')

      if (daysEl) {
        if (days > 0) {
          daysEl.innerHTML = `${days}<em>${d}</em>`
        } else {
          daysEl.parentElement?.remove()
        }
      }

      if (hoursEl) {
        if (hours > 0) {
          hoursEl.innerHTML = `${hours}<em>${h}</em>`
        } else {
          hoursEl.parentElement?.remove()
        }
      }

      if (minEl) minEl.innerHTML = `${min}<em>${m}</em>`
      if (secEl) secEl.innerHTML = `${sec}<em>${s}</em>`

      if (distance < 0) {
        clearInterval(this.timerId)
        this.innerHTML = textTimes.expired || 'Offer Expired'
      }
    }, 1000)
  }

  disconnectedCallback () {
    if (this.timerId) {
      clearInterval(this.timerId)
    }
  }
}
customElements.define('animated-countdown', AnimatedCountdown)

class ShareButton extends HTMLElement {
  constructor () {
    super()
    this.btn = this.querySelector('button')
    this.btn?.addEventListener('click', () => this.onBtnClick())
  }

  onBtnClick () {
    if (navigator.share) {
      navigator.share({
        title: this.btn?.dataset.shareTitle || document.title,
        url: window.location.href
      }).catch(() => {})
    } else if (this.btn && window.bootstrap?.Popover) {
      // Built as DOM nodes rather than an HTML string: the URL is attacker
      // influenced, and the popover previously ran with sanitize disabled.
      const group = document.createElement('div')
      group.className = 'input-group input-group-sm'

      const input = document.createElement('input')
      input.type = 'text'
      input.className = 'form-control text-truncate'
      input.readOnly = true
      input.value = window.location.href

      const copyBtn = document.createElement('button')
      copyBtn.id = 'share-btn-popover-btn'
      copyBtn.className = 'btn btn-outline-secondary'
      copyBtn.type = 'button'
      copyBtn.textContent = this.btn.dataset.textCopy || 'Copy'

      group.append(input, copyBtn)

      const popover = window.bootstrap.Popover.getOrCreateInstance(this.btn, {
        content: group,
        html: true,
        placement: 'top'
      })

      popover.show()

      document.querySelector('#share-btn-popover-btn')?.addEventListener('click', (e) => {
        navigator.clipboard.writeText(window.location.href)
        e.target.textContent = this.btn.dataset.textCopied || 'Copied!'
      })

      setTimeout(() => {
        popover.hide()
        this.btn?.blur()
      }, 5000)
    }
  }
}
customElements.define('share-button', ShareButton)

// Localization form
document.querySelectorAll('.shopify-localization-form button').forEach(btn => {
  btn.addEventListener('click', () => {
    const form = btn.closest('form')
    if (form) {
      const input = form.querySelector('[name="country_code"]')
      if (input) input.value = btn.dataset.isoCode
      form.submit()
    }
  })
})